import { useEffect } from 'react';
import { useMap } from 'react-leaflet';

/**
 * Haelt die Karte innerhalb des Bereichs, fuer den Kacheln existieren.
 *
 * Ein fester minZoom reicht dafuer nicht: Wie viel bei einer Zoomstufe sichtbar
 * ist, haengt von der Fenstergroesse ab. Auf einem breiten Monitor sieht man bei
 * z8 deutlich mehr als auf einem schmalen - und damit ueber den Rand der
 * Kachelabdeckung hinaus, wo nur noch leere Flaeche kommt.
 *
 * getBoundsZoom(bounds, true) liefert die Zoomstufe, bei der die Abdeckung das
 * Fenster gerade noch vollstaendig ausfuellt. Genau die ist das sinnvolle
 * Minimum.
 *
 * Die Pruefung laeuft bewusst nach jeder Zoom- und Bewegungsaenderung, nicht nur
 * einmal beim Einhaengen: Beim ersten Rendern steht die endgueltige Groesse des
 * Kartencontainers noch nicht fest, ein dort berechnetes Minimum waere zu klein
 * und wuerde nie wieder korrigiert.
 */
export default function FitToTileCoverage({ bounds, coreBounds }) {
  const map = useMap();

  useEffect(() => {
    if (!bounds) return undefined;

    const enforce = () => {
      const size = map.getSize();
      if (!size.x || !size.y) return; // Container noch ohne Groesse

      // Zoomstufe, ab der die Kachelabdeckung den Bildschirm ausfuellt
      const fillZoom = Math.ceil(map.getBoundsZoom(bounds, true));
      // Zoomstufe, bis zu der die Kernregion noch vollstaendig aufs Bild passt.
      // Auf schmalen Displays liegt sie unter fillZoom - dann hat sie Vorrang,
      // auch wenn dadurch ein Rand ohne Kacheln sichtbar wird. Ein Nutzer, der
      // das Saarland nicht als Ganzes sehen kann, ist schlechter dran als einer,
      // der aussen herum etwas Leere sieht.
      const coreZoom = coreBounds
        ? Math.floor(map.getBoundsZoom(coreBounds, false))
        : Infinity;
      const minZoom = Math.min(fillZoom, coreZoom);
      if (Number.isFinite(minZoom)) {
        if (map.getMinZoom() !== minZoom) {
          map.setMinZoom(minZoom);
        }
        if (map.getZoom() < minZoom) {
          // loest zoomend aus, beim naechsten Durchlauf greift die Bedingung nicht mehr
          map.setZoom(minZoom);
          return;
        }
      }

      // maxBounds samt Viscosity laesst bei ruckartigen Bewegungen (schnelles
      // Wischen, Trackpad-Schwung) durchaus zu, dass die Karte ueber den Rand
      // hinausrutscht und dort im Leeren stehen bleibt.
      // Nur zurueckholen, wenn die Abdeckung ueberhaupt groesser ist als das
      // Sichtfeld. Ist sie kleiner (schmales Display, siehe coreZoom oben),
      // liesse sich die Bedingung nie erfuellen und die Karte wuerde bei jeder
      // Bewegung herumspringen.
      const sicht = map.getBounds();
      const abdeckungGroesser =
        bounds.getWest() <= sicht.getWest() && bounds.getEast() >= sicht.getEast() &&
        bounds.getSouth() <= sicht.getSouth() && bounds.getNorth() >= sicht.getNorth();
      if (!abdeckungGroesser && bounds.contains(map.getCenter()) === false) {
        map.panInsideBounds(bounds, { animate: false });
      } else if (!bounds.contains(sicht) && map.getZoom() >= fillZoom) {
        map.panInsideBounds(bounds, { animate: false });
      }
    };

    map.whenReady(enforce);
    map.on('resize', enforce);
    map.on('zoomend', enforce);
    map.on('moveend', enforce);

    // Leaflet merkt nur Groessenaenderungen des Fensters, nicht die des eigenen
    // Containers. Waechst der per Layout (Seitenleiste klappt auf, Schriften
    // laden nach, Fenster wird skaliert), rechnet die Karte weiter mit der alten,
    // zu kleinen Groesse: sie laedt zu wenige Kacheln und laesst rechts und unten
    // eine Luecke, und das berechnete Zoom-Minimum ist ebenfalls zu klein.
    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        map.invalidateSize({ animate: false });
        enforce();
      });
      observer.observe(map.getContainer());
    }

    return () => {
      map.off('resize', enforce);
      map.off('zoomend', enforce);
      map.off('moveend', enforce);
      if (observer) observer.disconnect();
    };
  }, [map, bounds, coreBounds]);

  return null;
}
